import {
    arrow,
    offset,
    OffsetOptions,
    useClick,
    useDismiss,
    useFloating,
    UseFloatingOptions,
    useHover,
    useInteractions,
} from '@floating-ui/react';
import { useEffect, useRef, useState } from 'react';

interface OpenState {
    isPopoverOpen: boolean;
    setIsPopoverOpen: (isPopoverOpen: boolean) => void;
}

interface UseHoverPopoverProps {
    type: 'hover' | 'click';
    openState?: OpenState;
    options?: UseFloatingOptions;
    offsetOptions?: OffsetOptions;
    arrowSize?: {
        width?: number;
        height?: number;
    };
    allowDismiss?: boolean;
    arrowOffset?: boolean;
}

const DEFAULT_ARROW_SIZE = { width: 16, height: 8 };

export const usePopover = ({
    type,
    openState,
    options,
    offsetOptions,
    arrowSize,
    allowDismiss = type === 'click' ? true : false,
    arrowOffset = false,
}: UseHoverPopoverProps) => {
    let isPopoverOpen: OpenState['isPopoverOpen'];
    let setIsPopoverOpen: OpenState['setIsPopoverOpen'];

    if (openState) {
        isPopoverOpen = openState.isPopoverOpen;
        setIsPopoverOpen = openState.setIsPopoverOpen;
    } else {
        const openState = useState(false);
        isPopoverOpen = openState[0];
        setIsPopoverOpen = openState[1];
    }

    const arrowRef = useRef(null);

    const middleware = [arrow({ element: arrowRef })];
    if (arrowOffset) {
        middleware.unshift(offset(offsetOptions));
    } else {
        middleware.push(offset(offsetOptions));
    }

    const { refs, floatingStyles, context, update } = useFloating({
        open: isPopoverOpen,
        onOpenChange: setIsPopoverOpen,
        middleware,
        ...options,
    });
    const interactions = [type === 'click' ? useClick(context) : useHover(context)];
    if (allowDismiss) {
        interactions.push(useDismiss(context));
    }

    const { getReferenceProps, getFloatingProps } = useInteractions(interactions);

    useEffect(() => {
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [update]);

    return {
        isPopoverOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        arrowProps: { ...DEFAULT_ARROW_SIZE, ...arrowSize, ref: arrowRef, context },
    };
};
